// src/components/popups/PlanPaymentDrawer.js
import React, { useEffect, useMemo, useState } from 'react';
import './PlanPaymentDrawer.css';

export default function PlanPaymentDrawer({
  open,
  onClose,
  plan,              // selected plan object { id,label,pricePi,... }
  busy,
  onConfirmPi,       // () => Promise
  onRedeemCode,      // (code) => Promise
}) {
  const [method, setMethod] = useState('PI'); // 'PI' | 'CODE'
  const [code, setCode] = useState('');
  const isPremiumPlus = plan?.id === 'PREMIUM_PLUS';

  useEffect(() => {
    if (open) {
      setMethod('PI');
      setCode('');
    }
  }, [open]);

  const priceText = useMemo(() => {
    if (!plan) return '';
    return plan.pricePi > 0 ? `${plan.pricePi} Pi` : 'Free';
  }, [plan]);

  const canRedeem = isPremiumPlus && String(code || '').trim().length > 0;

  if (!open) return null;

  return (
    <div className="ppd-backdrop" onClick={onClose}>
      <div className="ppd-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="ppd-handle" />

        <div className="ppd-head">
          <div className="ppd-title">Choose payment method</div>
          <div className="ppd-sub">
            Upgrading to <span className="ppd-pill">{plan?.label || '—'}</span>
          </div>
        </div>

        {/* PI option */}
        <button
          className={`ppd-option ${method === 'PI' ? 'active' : ''}`}
          onClick={() => setMethod('PI')}
          type="button"
        >
          <div className="ppd-option-left">
            <div className="ppd-radio" />
            <div className="ppd-option-title">Pay with Pi</div>
          </div>
          <div className="ppd-amount">{priceText}</div>
        </button>

        {/* CODE option (only Premium+) */}
        <button
          className={`ppd-option ${method === 'CODE' ? 'active' : ''} ${isPremiumPlus ? '' : 'disabled'}`}
          onClick={() => {
            if (!isPremiumPlus) return;
            setMethod('CODE');
          }}
          disabled={!isPremiumPlus}
          type="button"
        >
          <div className="ppd-option-left">
            <div className="ppd-radio" />
            <div className="ppd-option-title">Use code</div>
          </div>
          <div className="ppd-amount">{isPremiumPlus ? '—' : 'Locked'}</div>
        </button>

        {/* Code input */}
        <div className={`ppd-code ${isPremiumPlus ? '' : 'locked'}`}>
          <div className="ppd-code-row">
            <div className="ppd-code-title">Reward code</div>
            {!isPremiumPlus && <div className="ppd-lock">Only Premium+</div>}
          </div>

          <input
            className="ppd-code-input"
            placeholder={isPremiumPlus ? 'Paste your code here' : 'Only Premium+ can use codes'}
            disabled={!isPremiumPlus || method !== 'CODE' || busy}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>

        <div className="ppd-actions">
          <button className="ppd-btn ghost" onClick={onClose} disabled={busy} type="button">
            Cancel
          </button>

          {method === 'PI' ? (
            <button
              className="ppd-btn primary"
              disabled={busy || !plan}
              onClick={onConfirmPi}
              type="button"
            >
              {busy ? 'Processing…' : `Confirm & Pay (${priceText})`}
            </button>
          ) : (
            <button
              className="ppd-btn primary"
              disabled={busy || !canRedeem}
              onClick={() => onRedeemCode && onRedeemCode(code)}
              type="button"
            >
              {busy ? 'Processing…' : 'Redeem code'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
